import { Button, Icon, Icons, SnackAlert, SnackAlertContent, Tooltip } from '@silverfort/sf-components';
import { FC, SVGProps, useCallback, useEffect, useRef, useState } from 'react';
import { useTowerLayout } from '../../../../TowerLayout/TowerLayoutProvider';
import { DisabledDisplayOptions } from '../../../../components/SystemSettings/hooks/lockedFeatures/types';
import { FeatureLock, TOOLTIP_PROP_OPTIONS } from '../../../../components/common/LockedFeatures';
import { httpGet } from '../../../../components/utils';
import ActionPopups, { PopupType } from '../components/ActionPopups';
import { ServiceAccountsExports } from '../components/ServiceAccountExports/ServiceAccountsExports';
import useGetRefSize from '../../../../hooks/useGetRefSize';
import { ServiceAccountTabs } from '../components/ServiceAccountTabs';
import { ServiceAccountActions } from '../containers/ServiceAccountBulkActions';
import ServiceAccountsFilters from '../containers/ServiceAccountsFilters';
import ServiceAccountsTable from '../containers/ServiceAccountsTable';
import ServiceAccountsDrawer from '../containers/drawer/ServiceAccountsDrawer';
import ServiceAccountsStatistics from '../containers/statistics/ServiceAccountsStatistics';
import { useServiceAccountContext } from '../contexts/ServiceAccountContext';

const syncStatusToTooltip = {
    success: 'Policy sync successful',
    fail: 'Policy sync failed',
    loading: 'Policies syncing'
};

const syncStatusToIconColor = {
    success: 'text-sf-positive-500',
    fail: 'text-sf-negative-500',
    loading: 'text-sf-gray-400'
};

interface ServiceAccountProps {
    openSmartPolicies: () => void;
}

const ServiceAccounts: FC<ServiceAccountProps> = ({ openSmartPolicies }) => {
    const tableRef = useRef(null);
    const { height: tableHeight } = useGetRefSize(tableRef);

    const {
        activeTab,
        setShowAccountRemovedAlert,
        showAccountRemovedAlert,
        onPremNHILicenseProtectionEnabled,
        onRemove,
        selectedServiceAccounts,
        rulesSyncStatus,
        setRulesSyncStatus
    } = useServiceAccountContext();
    const [openPopup, setOpenPopup] = useState<PopupType | undefined>();

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;

        const reloadRulesState = async () => {
            try {
                const response: { data: { RulesSynced: boolean } | null } = await httpGet('/policy/rulesSynced');
                if (response && response.data) {
                    const status = response.data.RulesSynced ? 'success' : 'loading';
                    setRulesSyncStatus(status);
                }
            } catch (error) {
                console.error('failed getting the rules sync status from server!', error);
                setRulesSyncStatus('fail');
            }

            timeoutId = setTimeout(() => {
                reloadRulesState();
            }, 30 * 1000);
        };
        reloadRulesState();

        return () => clearTimeout(timeoutId);
    }, []);

    const { setLayoutContent } = useTowerLayout();
    useEffect(() => {
        const layoutData = (
            <span>
                <Tooltip title={syncStatusToTooltip[rulesSyncStatus]} placement={'bottom-end'}>
                    <Icon
                        icon={Icons.Auto as FC<SVGProps<SVGSVGElement>>}
                        size={20}
                        className={syncStatusToIconColor[rulesSyncStatus]}
                    />
                </Tooltip>
            </span>
        );
        setLayoutContent(layoutData);
        return () => setLayoutContent('');
    }, [rulesSyncStatus, setLayoutContent]);

    const onClosePopup = useCallback(() => setOpenPopup(undefined), []);

    return (
        <div className="w-screen h-screen bg-sf-white">
            <div className="pt-16 px-6 overflow-auto h-full bg-sf-white ml-[60px] flex flex-col gap-6">
                <div className="flex flex-col pt-6">
                    <ServiceAccountsStatistics />
                </div>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-2">
                        <ServiceAccountsFilters />
                        <div className="flex gap-2">
                            <ServiceAccountsExports />
                            <FeatureLock
                                displayOptions={{
                                    enabled: onPremNHILicenseProtectionEnabled,
                                    disabledDisplay: DisabledDisplayOptions.TOOLTIP,
                                    tooltipProps: TOOLTIP_PROP_OPTIONS.NHI_PROTECTION_LICENSE_REQUIRED
                                }}>
                                <Button onClick={() => openSmartPolicies()} text={`Smart Policies`} />
                            </FeatureLock>
                        </div>
                    </div>
                    <div>
                        <ServiceAccountTabs />
                    </div>
                    <div className="flex-1" ref={tableRef}>
                        <ServiceAccountsTable tableHeight={tableHeight} />
                    </div>
                </div>
                <ServiceAccountsDrawer />
                {activeTab !== 'removed' && (
                    <ServiceAccountActions openSmartPolicies={openSmartPolicies} setOpenPopup={setOpenPopup} />
                )}
                <SnackAlert
                    anchorOrigin={{
                        horizontal: 'center',
                        vertical: 'bottom'
                    }}
                    open={showAccountRemovedAlert}>
                    <SnackAlertContent
                        message="This account has been moved to Discarded"
                        iconLeft={Icons.Block}
                        onClose={() => {
                            setShowAccountRemovedAlert(false);
                        }}
                    />
                </SnackAlert>
            </div>
            {!!openPopup && selectedServiceAccounts && (
                <ActionPopups
                    openPopup={openPopup}
                    onClosePopup={onClosePopup}
                    onRemove={onRemove}
                    serviceAccounts={selectedServiceAccounts}
                    onReinstate={() => {}}
                />
            )}
        </div>
    );
};

export default ServiceAccounts;
